/**
 * uploadRefusal.js — THE ONE READING OF A REFUSED UPLOAD (P2 Release 3; PROD-167, REP-08).
 *
 * Pure and import-free, so scripts/check-p2-access.mjs can run it under node.
 *
 * ★★★ WHY THIS EXISTS. Since orella-api P2-11, POST /files/upload sends every key through
 *   require_storage_key_access and answers a key the caller may not write with
 *     400 {'error': 'Invalid storage path', 'code': 'storage_path_invalid'}
 *     403 {'error': 'Access denied',        'code': 'storage_path_denied'}
 *   Before this file, three helpers in services/api.js answered ANY non-2xx from that route
 *   by reading the blob as base64 and returning the data: URL as if it were a stored
 *   reference, and filesAPI.uploadDataUrl (the card photos, the one live caller) turned the
 *   refusal into a bare null that no screen showed. So a wrong refusal would have been
 *   invisible to the patient — the card slot filled, the save dropped the image, and nobody
 *   was told. A refusal is now an error that carries its status and code, and the card
 *   screens say the photo was not saved.
 *
 * ★ What is kept on a card entry is uploadFailureOf(err): { status, code } and nothing
 *   else. No message text and never the storage path — the path names the patient's file.
 */

export const STORAGE_PATH_DENIED = 'storage_path_denied'
export const STORAGE_PATH_INVALID = 'storage_path_invalid'
// P17 step 4 (PROD-224): the two refusals orella-api P4 step 13 added to /files/upload,
//   413 {'code': 'upload_too_large'} over 25 MB and 415 {'code': 'upload_type_denied'} for a
//   type it will not store (an SVG is a declared active type and is refused).
export const UPLOAD_TOO_LARGE = 'upload_too_large'
export const UPLOAD_TYPE_DENIED = 'upload_type_denied'

export class UploadRefusedError extends Error {
  constructor({ status = 0, code = null, message = '' } = {}) {
    super(message || 'Upload failed')
    this.name = 'UploadRefusedError'
    this.status = status
    this.code = code
  }
}

/** Build the error for a fetch Response that was not ok. The body is read when it is JSON. */
export async function uploadRefusalFromResponse(response) {
  let body = null
  try { body = await response.json() } catch (_) { body = null }
  const b = body && typeof body === 'object' ? body : {}
  return new UploadRefusedError({
    status: Number(response && response.status) || 0,
    code: typeof b.code === 'string' && b.code ? b.code : null,
    message: typeof b.error === 'string' && b.error ? b.error : 'Upload failed',
  })
}

/** { status, code } of any thrown value; status 0 means the request never got an answer. */
export function uploadFailureOf(err) {
  if (!err || typeof err !== 'object') return { status: 0, code: null }
  return {
    status: Number(err.status) || 0,
    code: typeof err.code === 'string' && err.code ? err.code : null,
  }
}

const CARD_DENIED = 'Your card photo was not saved: the server refused to store it for this account. Please try again, and contact support if it keeps happening.'
const CARD_INVALID = 'Your card photo was not saved: the server rejected where it was to be stored. Please try again, and contact support if it keeps happening.'
const CARD_FAILED = 'Your card photo was not saved. Check your connection and add the photo again.'
const CARD_TOO_LARGE = 'This photo is too large to save. Take a new photo or choose a smaller file.'
const CARD_TYPE_DENIED = "This file type can't be saved. Please use a JPG or PNG photo."

/** The sentence a patient reads when a card photo was not saved. `t` is useI18n's t. */
export function cardUploadFailureMessage(t, failure) {
  const f = uploadFailureOf(failure)
  if (f.code === STORAGE_PATH_DENIED || f.status === 403) return t('cardScanner.uploadDenied', CARD_DENIED)
  if (f.code === STORAGE_PATH_INVALID || f.status === 400) return t('cardScanner.uploadInvalid', CARD_INVALID)
  // Keyed on STATUS as well as code: a body over the app's MAX_CONTENT_LENGTH (35 MB) is
  //   refused by werkzeug before the route runs, as an HTML 413 with no JSON code.
  if (f.code === UPLOAD_TOO_LARGE || f.status === 413) return t('cardScanner.uploadTooLarge', CARD_TOO_LARGE)
  if (f.code === UPLOAD_TYPE_DENIED || f.status === 415) return t('cardScanner.uploadTypeDenied', CARD_TYPE_DENIED)
  return t('cardScanner.uploadFailed', CARD_FAILED)
}
